import {FirebaseApp, initializeApp} from "firebase/app";
import {
  getAuth,
  Auth,
  connectAuthEmulator,
  onAuthStateChanged,
  User,
  IdTokenResult,
} from "firebase/auth";

import Config from "@/config";
import {
  isEstablishedUser,
  isPendingDeletion,
  isPhoneVerificationIssue,
  isVerifiedAge,
  UserFlag,
} from "@/hooks/useGetUserProfile";
import {resolveAuthDomain} from "./auth-domain";

export interface TokenClaims {
  email: string;
  email_verified: boolean;
  user_id: string;
  flags?: UserFlag[];
  roles?: Array<"superuser">;
}

export interface UserClaims {
  email: string;
  userId: string;
  isEmailVerified: boolean;
  isSuperuser: boolean;
  isPendingDeletion: boolean; // The user is waiting for account deletion.
  isEstablishedUser: boolean; // The user submitted an introset.
  isVerifiedAge: boolean; // The user has confirmed they are of legal age.
  isPhoneVerificationIssue: boolean; // The user's account is held due to a phone verification issue.
}

// Firebase Realtime Database constant for a timestamp.
export const TIMESTAMP = {".sv": "timestamp"};

const firebaseConfig = {
  apiKey: Config.FIREBASE_API_KEY,
  // Prefer the current host, so the OAuth redirect stays same-origin wherever
  // /__/auth is actually served. See auth-domain.ts.
  authDomain: resolveAuthDomain(
    window.location.hostname,
    Config.FIREBASE_AUTH_DOMAIN,
  ),
  databaseURL: Config.FIREBASE_DATABASE_URL,
  projectId: Config.FIREBASE_PROJECT_ID,
  storageBucket: Config.FIREBASE_STORAGE_BUCKET,
  messagingSenderId: Config.FIREBASE_MESSAGING_SENDER_ID,
  appId: Config.FIREBASE_APP_ID,
};

// initialize Firebase
export const firebaseApp: FirebaseApp = initializeApp(firebaseConfig);

// initialize Firebase Authentication and get a reference to the service
export const auth: Auth = getAuth(firebaseApp);

/**
 * Get the current user from Firebase Auth.
 * https://stackoverflow.com/a/76134506/1614237
 * @returns The current user or null if there is no user.
 */
export const getUser = async (): Promise<User | null> => {
  return new Promise((resolve) => {
    onAuthStateChanged(auth, (user) => {
      resolve(user);
    });
  });
};

/**
 * Get the Firebase Auth token or null if there is no user.
 * @param forceRefresh - Force refresh regardless of token expiration.
 */
export const getToken = async (
  forceRefresh = false,
): Promise<string | null> => {
  try {
    // verify the user is signed in
    const user = await getUser();

    // if there is no user, return null
    if (!user) return null;

    // Returns the current token if it has not expired. Otherwise,
    // this will refresh the token and return a new one.
    // https://firebase.google.com/docs/auth/admin/verify-id-tokens#retrieve_id_tokens_on_clients
    // https://firebase.google.com/docs/reference/js/v8/firebase.User#getidtoken
    return await user.getIdToken(forceRefresh);
  } catch (error) {
    console.error(error);
    return null;
  }
};

/**
 * Get the Firebase Auth token result or null if there is no user.
 * The token expires after one hour.
 * @param forceRefresh - Force refresh regardless of token expiration.
 */
export const getTokenResult = async (
  forceRefresh = false,
): Promise<IdTokenResult | null> => {
  try {
    const user = await getUser();

    // if there is no user, return null
    if (!user) return null;

    // Returns the current token result.
    // https://firebase.google.com/docs/reference/js/v8/firebase.User#getidtokenresult
    return await user.getIdTokenResult(forceRefresh);
  } catch (error) {
    console.error(error);
    return null;
  }
};

/**
 * Get the user claims from the Firebase Auth token.
 * @returns The user claims or null if there is no user.
 */
export const getUserClaims = async (): Promise<UserClaims | null> => {
  const tokenResult = await getTokenResult();
  if (!tokenResult) return null;

  const claims = tokenResult.claims as unknown as TokenClaims;

  const isSuperuser = claims?.roles?.includes("superuser") ?? false;

  return {
    email: claims.email,
    userId: claims.user_id,
    isEmailVerified: claims.email_verified,
    isSuperuser,
    isPendingDeletion: isPendingDeletion(claims?.flags),
    isEstablishedUser: isEstablishedUser(claims?.flags),
    isVerifiedAge: isVerifiedAge(claims?.flags),
    isPhoneVerificationIssue: isPhoneVerificationIssue(claims?.flags),
  };
};

// Connect to the Firebase Auth emulator if in development mode.
if (import.meta.env.VITE_AUTH_MODE === "emulator")
  connectAuthEmulator(auth, "http://localhost:9099");
